import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _styled from "styled-components";
var _CloseSmall;
var _excluded = ["className", "full", "plainText", "isDark", "textType", "responsive", "playButton", "clampOptions", "lang", "mediaUrl", "data-testid", "data-cy", "htmlProps", "userTagLink", "amplitude"];
import { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { ContentState, Editor, EditorState } from 'draft-js';
import CloseSmall from '@crehana/icons/outline/CloseSmall';
import { LANGUAGES } from '@crehana/ts-types';
import { PlayButton, TextLink } from "../Buttons";
import Dialog from "../Dialog";
import transition from "../styles/transition";
import CLASSNAMES_BY_TEXT_TYPE from "../Text/responsiveClassnames";
import Image from "./components/Image";
import LinkPreview from "./components/LinkPreview";
import STRINGS from "./i18n";
import { REGEX_BR } from "./regex";
import { createCompositeDecorator, extractImagesFromPlainText } from "./utils";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var StringParser = function StringParser(_ref) {
  var className = _ref.className,
    full = _ref.full,
    plainText = _ref.plainText,
    isDark = _ref.isDark,
    _ref$textType = _ref.textType,
    textType = _ref$textType === void 0 ? 'body3' : _ref$textType,
    responsive = _ref.responsive,
    playButton = _ref.playButton,
    clampOptions = _ref.clampOptions,
    _ref$lang = _ref.lang,
    lang = _ref$lang === void 0 ? LANGUAGES.ES : _ref$lang,
    mediaUrl = _ref.mediaUrl,
    dataTestId = _ref['data-testid'],
    dataCy = _ref['data-cy'],
    htmlProps = _ref.htmlProps,
    userTagLink = _ref.userTagLink,
    amplitude = _ref.amplitude,
    stringParserProps = _objectWithoutProperties(_ref, _excluded);
  var draftEditorRef = useRef(null);
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    images = _useState2[0],
    setImages = _useState2[1];
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    currentImagePreview = _useState4[0],
    setCurrentImagePreview = _useState4[1];
  var _useState5 = useState([]),
    _useState6 = _slicedToArray(_useState5, 2),
    linkUrls = _useState6[0],
    setLinkUrls = _useState6[1];
  var _useState7 = useState(!!(clampOptions !== null && clampOptions !== void 0 && clampOptions.initiallyClamped)),
    _useState8 = _slicedToArray(_useState7, 2),
    textIsShortened = _useState8[0],
    setTextIsShortened = _useState8[1];
  var textCanBeClamped = clampOptions && plainText.length > clampOptions.maxClampedChars;
  var onOpenImagePreview = function onOpenImagePreview(image) {
    setCurrentImagePreview(image);
  };
  var onCloseImagePreview = function onCloseImagePreview() {
    setCurrentImagePreview(undefined);
  };
  var onToggleShortenedText = function onToggleShortenedText() {
    setTextIsShortened(!textIsShortened);
  };
  var onNewLinkUrlFound = function onNewLinkUrlFound(linkUrl) {
    setLinkUrls(function (prevLinkUrls) {
      var uniqueUrlSet = new Set([].concat(_toConsumableArray(prevLinkUrls), [linkUrl]));
      return Array.from(uniqueUrlSet);
    });
  };
  var decoratorProps = {
    isDark: isDark,
    onNewLinkUrlFound: onNewLinkUrlFound,
    userTagLink: userTagLink,
    amplitude: amplitude
  };
  var _useState9 = useState(function () {
      return EditorState.createEmpty(createCompositeDecorator(decoratorProps));
    }),
    _useState0 = _slicedToArray(_useState9, 2),
    editorState = _useState0[0],
    setEditorState = _useState0[1];
  useEffect(function () {
    var _extractImagesFromPla = extractImagesFromPlainText({
        plainText: plainText,
        mediaUrl: mediaUrl
      }),
      imageList = _extractImagesFromPla.imageList,
      plainTextWithoutImages = _extractImagesFromPla.plainTextWithoutImages;
    setImages(imageList);
    var editorText = clampOptions && textCanBeClamped && textIsShortened ? "".concat(plainTextWithoutImages.substring(0, clampOptions.maxClampedChars), "...") : plainTextWithoutImages;
    var newEditorState = EditorState.createWithContent(
    // @ts-expect-error (Argument of type RegExp is not assignable to parameter of type string)
    ContentState.createFromText(editorText, REGEX_BR), createCompositeDecorator(decoratorProps));
    setEditorState(EditorState.moveSelectionToEnd(newEditorState));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plainText, textIsShortened]);
  var onChange = function onChange(newEditorState) {
    setEditorState(newEditorState);
  };
  var fontClassNames = CLASSNAMES_BY_TEXT_TYPE(responsive)[textType];
  return /*#__PURE__*/_jsxs(_StyledDiv, _objectSpread(_objectSpread(_objectSpread({
    className: cn('flex flex-col items-start', {
      'w-full': full
    }, fontClassNames, isDark ? 'text-white' : 'text-base-main', className)
  }, stringParserProps), htmlProps), {}, {
    $_css: transition('color'),
    children: [/*#__PURE__*/_jsx(Editor, {
      ref: draftEditorRef,
      editorState: editorState,
      onChange: onChange,
      readOnly: true,
      "data-testid": dataTestId,
      "data-cy": dataCy,
      blockStyleFn: function blockStyleFn(contentBlock) {
        var blockText = contentBlock.getText();
        var blockDepth = contentBlock.getDepth();
        if (blockDepth === 0 && blockText.length) {
          return 'mb-8';
        }
        return '';
      }
    }), textCanBeClamped && /*#__PURE__*/_jsx(TextLink, {
      label: textIsShortened ? STRINGS[lang].SHOW_MORE : STRINGS[lang].SHOW_LESS,
      type: "level3",
      onClick: onToggleShortenedText
    }), playButton && /*#__PURE__*/_jsx(PlayButton, _objectSpread({
      className: textCanBeClamped ? 'mt-16' : 'mt-8',
      isDark: isDark
    }, playButton)), images.length > 0 && /*#__PURE__*/_jsx("div", {
      className: cn('flex flex-wrap', playButton ? 'mt-16' : 'mt-8', {
        '-m-4': images.length > 1
      }),
      children: images.map(function (image) {
        return /*#__PURE__*/_jsx(Image, _objectSpread(_objectSpread({}, image), {}, {
          className: images.length > 1 ? 'p-4' : '',
          onOpenImagePreview: onOpenImagePreview,
          isDark: isDark
        }), "string-parser-img-".concat(image.name));
      })
    }), linkUrls.map(function (url) {
      return /*#__PURE__*/_jsx(LinkPreview, {
        className: playButton || images.length > 0 ? 'mt-16' : 'mt-8',
        url: url,
        isDark: isDark
      }, "string-parser-link-".concat(url));
    }), /*#__PURE__*/_jsx(Dialog, {
      open: !!currentImagePreview,
      onDismiss: onCloseImagePreview,
      hideDialogSheet: true,
      children: currentImagePreview && /*#__PURE__*/_jsxs("div", {
        className: "relative flex items-center justify-center w-full h-full p-56 z-1",
        role: "document",
        tabIndex: -1,
        children: [/*#__PURE__*/_jsx("button", {
          type: "button",
          className: "absolute top-0 right-0 mt-16 mr-16 focus:outline-none z-dialog text-base-lighter sm:mt-32 sm:mr-32",
          onClick: onCloseImagePreview,
          "data-testid": "close-dialog",
          children: _CloseSmall || (_CloseSmall = /*#__PURE__*/_jsx(CloseSmall, {
            className: "w-24 h-24 fill-current sm:w-32 sm:h-32 lg:w-48 lg:h-48",
            ignoreColor: true
          }))
        }), /*#__PURE__*/_jsx("img", {
          className: "max-w-full max-h-full bg-base-lighter-56",
          src: currentImagePreview.url,
          alt: currentImagePreview.name
        })]
      })
    })]
  }));
};
export default StringParser;
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-1ij0d5f-0"
})(["\n        overflow-wrap: break-word;\n        word-break: break-word;\n        transition: ", ";\n      "], function (p) {
  return p.$_css;
});